import { Button } from "@/components/ui/button";
import type { BooMapLink, ContainerRow } from "@/lib/api";
import { booConnections, orderBoos } from "@/lib/boo-order";
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";
import { BooAccessLine } from "./BooAccessLine";
import { BooMark } from "./BooMark";
import { BooMenu } from "./BooMenu";
import { booStatus } from "./boo-status";

// The Boos as rows, each with an Open button. Same order as the diagram: a
// Boo comes after every Boo that learns from it.
export function BooList({
  boos,
  links,
  onOpen,
  onLink,
  onOpenSharing,
}: {
  boos: ContainerRow[];
  links: BooMapLink[];
  onOpen: (boo: ContainerRow) => void;
  onLink?: (boo: ContainerRow) => void;
  onOpenSharing?: (boo: ContainerRow) => void;
}) {
  const name = new Map(boos.map((b) => [b.id, b.name]));
  const names = (ids: string[]) => ids.map((id) => name.get(id) ?? "a Boo").join(", ");
  return (
    <ul className="flex flex-col divide-y divide-border overflow-hidden rounded-xl bg-card shadow-[0_0_0_1px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.09)]">
      {orderBoos(boos, links).map((boo) => {
        const running = boo.deploymentStatus === "running";
        const status = booStatus(boo.deploymentStatus);
        const { uses, usedBy, waiting } = booConnections(boo.id, links);
        const linkLine = [
          uses.length > 0 && `Learns from ${names(uses)}`,
          usedBy.length > 0 && `Shares with ${names(usedBy)}`,
          waiting > 0 && `${waiting} ${waiting === 1 ? "request" : "requests"} waiting for approval`,
        ].filter(Boolean);
        return (
          <li
            key={boo.id}
            onClick={() => onOpen(boo)}
            title={boo.errorMessage ?? undefined}
            className={cn(
              "group flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/40",
              !running && "bg-muted/20",
            )}
          >
            <BooMark id={boo.id} muted={!running} className="size-8" />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-sm font-semibold" title={boo.name}>
                  {boo.name}
                </span>
                <span
                  className={cn("size-2 shrink-0 rounded-full", status.dot)}
                  aria-label={status.label ?? "Active"}
                />
                {status.label && (
                  <span className="shrink-0 text-xs text-muted-foreground">{status.label}</span>
                )}
              </div>
              <BooAccessLine container={boo} />
              {linkLine.length > 0 && (
                <span className="text-xs text-muted-foreground text-pretty">
                  {linkLine.join(" · ")}
                </span>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 transition-transform active:scale-[0.96]"
              aria-label={`Open ${boo.name}`}
              onClick={(e) => {
                e.stopPropagation();
                onOpen(boo);
              }}
            >
              Open
              <ArrowRight className="transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" />
            </Button>
            <BooMenu boo={boo} onLink={onLink} onOpenSharing={onOpenSharing} />
          </li>
        );
      })}
    </ul>
  );
}
